import { Injectable } from '@angular/core';
import { IMachineProduction } from 'src/app/modules/widget/interfaces/machine-production';
import { IProductionHour } from 'src/app/modules/widget/interfaces/production-hour';
import moment from 'moment';
import { LanguageService } from 'projects/common/src/app/services/parameters';
import { firstLetterUpper } from 'projects/common/src/app/services/utils/utilities';

@Injectable({ providedIn: 'root' })
export class MachineProductionService {
  constructor(private langService: LanguageService) {}

  /**
   * Function to create Jumbo series for Total Production (by hour)
   */
  public createProductionSerieByHour(machine: string, productions: IProductionHour[]): IMachineProduction {
    const barSerie = {
      dataLabels: {
        enabled: true,
        color: '#22D983',
        fontSize: '10px',
      },
      meditionUnit: { singularUnit: '', pluralUnit: '' },
      name: '',
      type: 'column',
      data: productions.map((value) => this.calculateFromAxisDate(value, 'count', productions)),
      itemType: 1,
      color: 'rgba(34, 217, 131, 0.5)',
      tooltip: {
        valueSuffix: ' mts',
      },
    };

    const firstLineSerie = {
      dataLabels: {
        enabled: true,
        color: '#707070',
        fontSize: '10px',
      },
      name: '',
      type: 'line',
      color: '#707070',
      data: productions.map((value) => this.calculateFromAxisDate(value, 'cumulative', productions)),
      yAxis: 1,
      dashStyle: 'Dash',
      meditionUnit: { singularUnit: '', pluralUnit: '' },
      tooltip: {
        valueSuffix: ' mts',
      },
      itemType: 2,
    };

    const expectedLineSerie = {
      dataLabels: {
        enable: true,
        color: '#FF9966',
        fontSize: '10px',
      },
      name: '',
      type: 'line',
      color: '#FF9966',
      data: this.calculateExpectedData('expected', productions),
      yAxis: 1,
      meditionUnit: { singularUnit: '', pluralUnit: '' },
      tooltip: {
        valueSuffix: ' mts',
      },
      itemType: 3,
    };

    let xAxisLeft = '';
    let xAxisRight = '';

    switch (machine) {
      case 'jumbo':
        barSerie.name = firstLetterUpper(this.langService.message('drillings_mts'));
        firstLineSerie.name = firstLetterUpper(this.langService.message('drillings_cumulative'));
        xAxisLeft = 'drillings_mts';
        xAxisRight = 'drillings_cumulative';
        barSerie.meditionUnit = {
          singularUnit: this.langService.message('drillings_mts_short'),
          pluralUnit: this.langService.message('drillings_mts_short'),
        };
        firstLineSerie.meditionUnit = {
          singularUnit: this.langService.message('drillings_cumulative_short'),
          pluralUnit: this.langService.message('drillings_cumulative_short'),
        };
        break;
      case 'train':
        barSerie.name = firstLetterUpper(this.langService.message('vueltas'));
        firstLineSerie.name = firstLetterUpper(this.langService.message('cumulative_loops'));
        xAxisLeft = 'loops';
        xAxisRight = 'loops_cumulative';
        barSerie.meditionUnit = { singularUnit: this.langService.message('loops'), pluralUnit: '' };
        firstLineSerie.meditionUnit = { singularUnit: this.langService.message('loops_cumulative'), pluralUnit: '' };
        break;

      case 'lhd':
        barSerie.name = firstLetterUpper(this.langService.message('shelves'));
        firstLineSerie.name = firstLetterUpper(this.langService.message('cumulative_production'));
        expectedLineSerie.name = firstLetterUpper(this.langService.message('expected_production'));
        xAxisLeft = 'shelves';
        xAxisRight = 'cumulative_production';
        barSerie.meditionUnit = { singularUnit: this.langService.message('production'), pluralUnit: this.langService.message('shelves') };
        firstLineSerie.meditionUnit = {
          singularUnit: this.langService.message('cumulative_production'),
          pluralUnit: this.langService.message('cumulative_production'),
        };
        expectedLineSerie.meditionUnit = {
          singularUnit: this.langService.message('expected_production'),
          pluralUnit: this.langService.message('expected_production'),
        };
        break;

      case 'truck':
        barSerie.name = firstLetterUpper(this.langService.message('loops'));
        firstLineSerie.name = firstLetterUpper(this.langService.message('loops-acumulative'));
        xAxisLeft = 'loops';
        xAxisRight = 'loops-acumulative';
        barSerie.meditionUnit = { singularUnit: this.langService.message('transfers'), pluralUnit: this.langService.message('loops') };
        firstLineSerie.meditionUnit = { singularUnit: 'Vuelta', pluralUnit: this.langService.message('cumulative_loops') };
        break;

      default:
        break;
    }

    return { barSerie, firstLineSerie, xAxisLeft, xAxisRight, expectedLineSerie };
  }

  /**
   * Function to get the Y value from X axis
   *
   * @param date Date to compare
   */
  private calculateFromAxisDate(production: IProductionHour, type: string, productions: IProductionHour[]): number {
    const dateValue = moment(production.startDate, 'YYYY-MM-DD HH:mm:ss');
    const currentDate = moment(new Date(), 'YYYY-MM-DD HH:mm:ss');

    const isHigher = dateValue.format('YYYY-MM-DD') > currentDate.format('YYYY-MM-DD');
    const isEqual = dateValue.format('YYYY-MM-DD') === currentDate.format('YYYY-MM-DD');

    if (isHigher || isEqual) {
      if (dateValue.hours() <= currentDate.hours()) {
        if (isEqual) {
          if (dateValue.hours() === currentDate.hours() && type === 'cumulative') {
            return Math.max(...productions.map((value) => value.itemCumulative));
          }
          return type === 'cumulative' ? production.itemCumulative : production.itemCount;
        }
        if (isHigher) {
          return null;
        }
      } else {
        return null;
      }
    } else {
      return type === 'cumulative' ? production.itemCumulative : production.itemCount;
    }
  }

  private calculateExpectedData(type: string, productions: IProductionHour[]): any {
    if (type === 'expected') {
      const dataExpected = [];
      productions.forEach((p) => {
        dataExpected.push(p.itemExpected);
      });
      return dataExpected;
    }
  }
}
